import type { AppJwtClaimsParsed } from "./jwt-payload";
import { decodeAppJwtClaims, jwtExpiresAt } from "./jwt-payload";

/** How often an open tab asks for a fresh app JWT. */
export const SESSION_KEEPALIVE_INTERVAL_MS = 15 * 60 * 1000;

/** Renew before the server expiry so a class does not hit the end of the sign-in. */
export const SESSION_REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * School Chromebook clocks are often a few minutes fast. Keep using a token the
 * server may still accept, and let a 401 decide when it is really dead.
 */
export const SESSION_CLOCK_GRACE_SECONDS = 15 * 60;

const MIN_KEEPALIVE_DELAY_MS = 30_000;

export const SIGN_IN_TO_CHECK_MESSAGE =
  "Your sign-in expired. Sign in again to check your work. Your code is still in this editor.";

export const SIGN_IN_TO_SAVE_MESSAGE =
  "Your sign-in expired. Sign in again to save this project. Your latest edits are kept in this browser.";

export function isAppJwtUsable(token: string, nowMs = Date.now()): boolean {
  if (!decodeAppJwtClaims(token)) return false;
  const exp = jwtExpiresAt(token);
  if (exp == null) return false;
  return nowMs / 1000 < exp + SESSION_CLOCK_GRACE_SECONDS;
}

/** Impersonation sessions are not slid forward. */
export function shouldAttemptSessionRefresh(token: string, nowMs = Date.now()): boolean {
  const claims = decodeAppJwtClaims(token);
  if (!claims || claims.impersonation) return false;
  return isAppJwtUsable(token, nowMs);
}

export function nextKeepaliveDelayMs(token: string | null, nowMs = Date.now()): number {
  if (!token) return SESSION_KEEPALIVE_INTERVAL_MS;
  const exp = jwtExpiresAt(token);
  if (exp == null) return SESSION_KEEPALIVE_INTERVAL_MS;
  const untilRefresh = exp * 1000 - SESSION_REFRESH_BEFORE_EXPIRY_MS - nowMs;
  if (untilRefresh < MIN_KEEPALIVE_DELAY_MS) return MIN_KEEPALIVE_DELAY_MS;
  return Math.min(untilRefresh, SESSION_KEEPALIVE_INTERVAL_MS);
}

export function sameSessionClaims(a: AppJwtClaimsParsed | null, b: AppJwtClaimsParsed | null): boolean {
  if (!a || !b) return false;
  return (
    a.sub === b.sub &&
    a.email === b.email &&
    a.role === b.role &&
    a.enrollmentSummary === b.enrollmentSummary &&
    Boolean(a.impersonation) === Boolean(b.impersonation)
  );
}

export function isUnauthorizedError(error: unknown): boolean {
  if (error instanceof Error && error.name === "ApiClientError") {
    const status = (error as Error & { status?: unknown }).status;
    if (status === 401) return true;
  }
  if (!(error instanceof Error)) return false;
  if (/\(401\)/.test(error.message)) return true;
  return error.message.trim().toLowerCase() === "unauthorized";
}

export function userFacingRequestError(error: unknown, fallback: string, unauthorizedMessage: string): string {
  if (isUnauthorizedError(error)) return unauthorizedMessage;
  return error instanceof Error && error.message ? error.message : fallback;
}
